import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api'

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com'

const PendingGoals = () => {
  const baseUrl = API_BASE_URL
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const employeeId = location.state?.employeeId || id

  const [loading, setLoading] = useState(false)
  const [goals, setGoals] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const rowsPerPage = 20

  const [expandedTexts, setExpandedTexts] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedGoal, setSelectedGoal] = useState(null)
  const [approvalLoading, setApprovalLoading] = useState(null) // goal id or null

  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectGoal, setRejectGoal] = useState(null)
  const [rejectSubmitting, setRejectSubmitting] = useState(false)
  const [rejectError, setRejectError] = useState(null)

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedGoal(null)
  }

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }))
  }

  const renderTruncatedText = (text, goalId, field, maxLength = 45) => {
    const safeText = (text ?? '').toString()
    if (!safeText) return ''
    if (safeText.length <= maxLength) return safeText

    const key = `${goalId || 'unknown'}-${field}`
    const isExpanded = !!expandedTexts[key]
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`

    return (
      <span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          className="btn btn-link btn-sm p-0 ms-1 text-primary text-decoration-none fw-semibold fs-12"
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    )
  }

  const fetchPendingGoals = async () => {
    setLoading(true)
    try {
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      const pendingGoals = Array.isArray(response.data)
        ? response.data.filter(goal => goal.approval_status === 'pending')
        : []
      setGoals(pendingGoals)
    } catch (err) {
      toast.error('Failed to fetch pending goals.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPendingGoals()
  }, [baseUrl])

  useEffect(() => {
    setCurrentPage(1)
  }, [goals])

  const handleApproveGoal = async (goal) => {
    setApprovalLoading(goal.id)
    try {
      const token = Cookies.get('token')
      await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      toast.success('Goal approved successfully!')
      fetchPendingGoals()
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to approve goal.')
    } finally {
      setApprovalLoading(null)
    }
  }

  const openRejectModal = (goal) => {
    setRejectGoal(goal)
    setRejectReason('')
    setRejectError(null)
    setRejectModalOpen(true)
  }

  const closeRejectModal = () => {
    setRejectModalOpen(false)
    setRejectGoal(null)
    setRejectReason('')
    setRejectError(null)
  }

  const handleRejectSubmit = async (e) => {
    e.preventDefault()
    if (!rejectReason.trim()) {
      setRejectError('Please enter a rejection reason.')
      return
    }
    setRejectSubmitting(true)
    setRejectError(null)
    try {
      const token = Cookies.get('token')
      await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      toast.success('Goal rejected successfully!')
      fetchPendingGoals()
      closeRejectModal()
    } catch (err) {
      setRejectError(err.response?.data?.message || err.message || 'Failed to reject goal.')
    } finally {
      setRejectSubmitting(false)
    }
  }

  const totalPages = Math.ceil(goals.length / rowsPerPage) || 1
  const indexOfLastRow = currentPage * rowsPerPage
  const indexOfFirstRow = indexOfLastRow - rowsPerPage
  const currentGoals = goals.slice(indexOfFirstRow, indexOfLastRow)

  const getKraLabel = (goal) => {
    const name = goal?.key_result_area?.name || goal?.name || goal?.key_result_area
    return (name ?? 'Uncategorized').toString()
  }

  const sortedCurrentGoals = [...currentGoals].sort((a, b) => {
    const an = (a?.employee_name || '').toString()
    const bn = (b?.employee_name || '').toString()
    const empCompare = an.localeCompare(bn, undefined, { sensitivity: 'base' })
    if (empCompare !== 0) return empCompare

    const ak = getKraLabel(a)
    const bk = getKraLabel(b)
    const kraCompare = ak.localeCompare(bk, undefined, { sensitivity: 'base' })
    if (kraCompare !== 0) return kraCompare

    const aa = (a?.activity || '').toString()
    const ba = (b?.activity || '').toString()
    return aa.localeCompare(ba, undefined, { sensitivity: 'base' })
  })

  const currentPageCountsByEmployee = sortedCurrentGoals.reduce((acc, g) => {
    const key = (g?.employee_name || 'Unknown Employee').toString()
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return
    setCurrentPage(newPage)
  }

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <ToastContainer position="top-right" autoClose={3000} />
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
            <div className="my-auto mb-2 mb-md-0">
              <h4 className="page-title mb-0">Pending Goals & KPIs</h4>
              <p className="text-muted mb-0 mt-1">Review and action pending Key Performance Indicators submitted for your approval.</p>
            </div>
            <div>
              <span className="badge bg-warning-subtle text-warning border border-warning px-3 py-2 fs-13 fw-semibold">
                <i className="ti ti-clock me-1"></i> {goals.length} Pending Approval
              </span>
            </div>
          </div>

          {/* Main Card */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0 text-primary fw-bold">
                <i className="ti ti-list-check me-2"></i>Pending Approvals
              </h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="text-muted fw-normal mb-0">Loading pending KPIs...</h6>
                </div>
              ) : goals.length === 0 ? (
                <div className="text-center py-5">
                  <div className="avatar avatar-xl bg-light-success text-success rounded-circle mb-3 mx-auto d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                    <i className="ti ti-circle-check fs-28"></i>
                  </div>
                  <h5 className="text-dark fw-bold mb-1">No Pending KPIs</h5>
                  <p className="text-muted mb-0">All submitted goals have been approved or processed.</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: '220px' }}>Activity / KPI</th>
                          <th style={{ minWidth: '140px' }}>Stakeholder</th>
                          <th className="text-center" style={{ width: '120px' }}>RAG Color</th>
                          <th className="text-center" style={{ width: '100px' }}>Quarter</th>
                          <th className="text-center" style={{ width: '90px' }}>Metric</th>
                          <th className="text-center" style={{ width: '110px' }}>Status</th>
                          <th className="text-center" style={{ width: '140px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCurrentGoals.map((goal, idx) => {
                          const employeeName = (goal?.employee_name || 'Unknown Employee').toString()
                          const prevEmployeeName = idx > 0
                            ? (sortedCurrentGoals[idx - 1]?.employee_name || 'Unknown Employee').toString()
                            : null
                          const showEmployeeHeader = idx === 0 || employeeName !== prevEmployeeName

                          const kraLabel = getKraLabel(goal)
                          const prevKraLabel = idx > 0 ? getKraLabel(sortedCurrentGoals[idx - 1]) : null
                          const showKraHeader = showEmployeeHeader || kraLabel !== prevKraLabel

                          const kraPercentage =
                            goal?.key_result_area?.percentage ??
                            goal?.kra?.percentage ??
                            goal?.percentage

                          return (
                            <React.Fragment key={goal.id || `${employeeName}-${idx}`}>
                              {/* Employee Header Row */}
                              {showEmployeeHeader && (
                                <tr className="table-primary bg-primary-subtle">
                                  <td colSpan={7} className="fw-bold py-2 text-primary">
                                    <i className="ti ti-user me-2"></i>
                                    <span>{employeeName}</span>
                                    <span className="badge bg-primary ms-2 font-weight-normal fs-11">
                                      {currentPageCountsByEmployee[employeeName]} pending {currentPageCountsByEmployee[employeeName] === 1 ? 'KPI' : 'KPIs'}
                                    </span>
                                  </td>
                                </tr>
                              )}

                              {/* KRA Sub-header Row */}
                              {showKraHeader && (
                                <tr className="table-secondary bg-light-subtle">
                                  <td colSpan={7} className="fw-bold py-2 text-dark ps-4">
                                    <i className="ti ti-target me-2 text-muted"></i>
                                    <span>{kraLabel}</span>
                                    <span className="badge bg-secondary ms-2 font-weight-normal fs-11">
                                      KRA Weight: {kraPercentage !== undefined && kraPercentage !== null ? `${kraPercentage}%` : '-'}
                                    </span>
                                  </td>
                                </tr>
                              )}

                              {/* Goal Data Row */}
                              <tr>
                                <td>
                                  {renderTruncatedText(
                                    goal.activity,
                                    goal.id || `${idx + indexOfFirstRow}`,
                                    'activity',
                                    50
                                  )}
                                </td>
                                <td>{goal.stakeholder || '-'}</td>
                                <td className="text-center">
                                  {goal.progress_indicator || goal.rag_color ? (
                                    <span
                                      className="badge rounded-pill px-3 py-1 text-uppercase font-weight-bold"
                                      style={{
                                        backgroundColor: goal.progress_indicator || '#6c757d',
                                        color: '#fff',
                                        fontSize: '11px'
                                      }}
                                    >
                                      {goal.rag_color || 'RAG'}
                                    </span>
                                  ) : (
                                    <span className="badge bg-light-secondary text-secondary">N/A</span>
                                  )}
                                </td>
                                <td className="text-center font-weight-semibold">{goal.quarter || '-'}</td>
                                <td className="text-center font-weight-bold text-dark">{goal.metric !== undefined ? `${goal.metric}%` : '-'}</td>
                                <td className="text-center">
                                  <span className="badge bg-warning-subtle text-warning border border-warning px-2 py-1">
                                    <i className="ti ti-clock me-1"></i> Pending
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex align-items-center justify-content-center gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-outline-primary rounded-circle"
                                      title="View Details"
                                      onClick={() => handleViewGoal(goal)}
                                    >
                                      <i className="ti ti-eye"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-outline-success rounded-circle"
                                      title="Approve Goal"
                                      onClick={() => handleApproveGoal(goal)}
                                      disabled={approvalLoading === goal.id}
                                    >
                                      {approvalLoading === goal.id ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      ) : (
                                        <i className="ti ti-check fs-16"></i>
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-outline-danger rounded-circle"
                                      title="Reject Goal"
                                      onClick={() => openRejectModal(goal)}
                                      disabled={approvalLoading === goal.id}
                                    >
                                      <i className="ti ti-x fs-16"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Footer */}
                  {goals.length > rowsPerPage && (
                    <div className="card-footer bg-transparent border-top py-3 d-flex align-items-center justify-content-between">
                      <div className="text-muted fs-13">
                        Showing <span className="fw-bold">{indexOfFirstRow + 1}</span> to <span className="fw-bold">{Math.min(indexOfLastRow, goals.length)}</span> of <span className="fw-bold">{goals.length}</span> pending goals
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="ti ti-chevron-left me-1"></i> Previous
                        </button>
                        <span className="fs-13 fw-semibold text-muted px-2">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next <i className="ti ti-chevron-right ms-1"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Goal Details Modal */}
      {modalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-light border-bottom">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                  <i className="ti ti-file-description text-primary fs-20"></i>
                  <span>Pending KPI Details</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="table-responsive rounded border">
                  <table className="table table-striped table-borderless align-middle mb-0">
                    <tbody>
                      <tr>
                        <td className="fw-semibold text-muted" style={{ width: '35%' }}>Employee Name:</td>
                        <td className="fw-bold text-primary">{selectedGoal.employee_name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Key Result Area:</td>
                        <td className="fw-bold text-dark">{selectedGoal.key_result_area?.name || selectedGoal.name || 'Uncategorized'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">KRA Weight:</td>
                        <td className="fw-bold text-dark">{selectedGoal.key_result_area?.percentage || selectedGoal.percentage || '-'}%</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Activity / KPI:</td>
                        <td className="text-dark">{selectedGoal.activity || '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">KPI Metric:</td>
                        <td className="fw-bold text-dark">{selectedGoal.metric}%</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Stakeholder:</td>
                        <td className="text-dark">{selectedGoal.stakeholder || '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Quarter:</td>
                        <td className="fw-bold text-dark">{selectedGoal.quarter || '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">RAG Indicator:</td>
                        <td>
                          {selectedGoal.progress_indicator || selectedGoal.rag_color ? (
                            <span
                              className="badge rounded-pill px-3 py-1 text-uppercase fw-bold"
                              style={{
                                backgroundColor: selectedGoal.progress_indicator || '#6c757d',
                                color: '#fff',
                                fontSize: '11px'
                              }}
                            >
                              {selectedGoal.rag_color || 'RAG'}
                            </span>
                          ) : (
                            <span className="badge bg-light-secondary text-secondary">N/A</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Completion Status:</td>
                        <td className="text-capitalize text-dark">{selectedGoal.status ? selectedGoal.status.replace(/_/g, ' ') : 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Is Overdue:</td>
                        <td>
                          {selectedGoal.is_overdue ? (
                            <span className="badge bg-danger-subtle text-danger">Yes</span>
                          ) : (
                            <span className="badge bg-success-subtle text-success">No</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Approval Status:</td>
                        <td>
                          <span className="badge bg-warning-subtle text-warning border border-warning px-2 py-1">
                            Pending
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Due Date:</td>
                        <td className="text-dark">
                          {selectedGoal.completion_date
                            ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light border-top d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={closeModal}
                >
                  Close
                </button>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-danger d-flex align-items-center gap-2"
                    onClick={() => {
                      closeModal()
                      openRejectModal(selectedGoal)
                    }}
                  >
                    <i className="ti ti-x"></i> Reject
                  </button>
                  <button
                    type="button"
                    className="btn btn-success d-flex align-items-center gap-2"
                    onClick={() => {
                      closeModal()
                      handleApproveGoal(selectedGoal)
                    }}
                  >
                    <i className="ti ti-check"></i> Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && rejectGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2">
                  <i className="ti ti-alert-circle fs-20"></i>
                  <span>Reject KPI Approval</span>
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeRejectModal}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleRejectSubmit}>
                <div className="modal-body p-4">
                  <p className="text-muted fs-14 mb-3">
                    Please provide a clear reason for rejecting the goal for <strong className="text-dark">{rejectGoal.employee_name || 'this employee'}</strong>:
                  </p>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Rejection Reason <span className="text-danger">*</span></label>
                    <textarea
                      name="rejection_reason"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      required
                      className="form-control"
                      placeholder="Enter reason for rejection..."
                      rows={4}
                      disabled={rejectSubmitting}
                    ></textarea>
                  </div>
                  {rejectError && (
                    <div className="alert alert-danger py-2 fs-13 mb-0" role="alert">
                      <i className="ti ti-alert-triangle me-1"></i>{rejectError}
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light border-top">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closeRejectModal}
                    disabled={rejectSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-danger px-4 d-flex align-items-center gap-2"
                    disabled={rejectSubmitting}
                  >
                    {rejectSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Rejecting...</span>
                      </>
                    ) : (
                      <>
                        <i className="ti ti-x"></i>
                        <span>Reject KPI</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PendingGoals